"use client";

import { useCallback, useState } from "react";
import { useAction, useConvexAuth } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { CheckoutShipment } from "@convex/schemas/validators/checkoutShipment.lib";
import { getGuestCheckoutSessionId } from "@/lib/checkout/guest-checkout-session";
import { track } from "@/lib/analytics/tracker";
import { readPosthogDistinctId } from "@/lib/analytics/posthog-identity";
import { handleError } from "@/lib/handle-error";
import {
  checkoutRowsNow,
  rememberCheckoutRows,
} from "@/hooks/cart/use-guest-cart";
import {
  useGuestContactPrompt,
  type GuestCheckoutContact,
} from "./use-guest-contact-prompt";

export interface DirectCheckoutItem {
  productId: Id<"products">;
  quantity: number;
  unitPriceCents: number;
}

export interface DirectCheckoutRequest {
  source: "cart" | "listing";
  items: DirectCheckoutItem[];
  shipments: CheckoutShipment[];
  expectedFreightCents?: number;
  expectedProductSubtotals?: Array<{
    productId: Id<"products">;
    subtotalCents: number;
  }>;
  buyNowLine?: { productId: Id<"products">; quantity: number };
  contact?: GuestCheckoutContact;
}

export function deliveryFromShipments(shipments: CheckoutShipment[]) {
  const [first] = shipments;
  return {
    line1: first.line1 || undefined,
    city: first.city,
    state: first.state,
    postalCode: first.postalCode,
    country: first.country,
    deliveryDate: first.deliveryDate,
    deliveryWindowEnd: first.deliveryWindowEnd ?? first.deliveryDate,
    hasDockOnSite: first.hasDockOnSite,
    siteAccess: first.siteAccess,
  };
}

function trackCheckoutStarted(
  items: DirectCheckoutItem[],
  surface: DirectCheckoutRequest["source"],
) {
  track("checkout.started", {
    surface,
    itemCount: items.reduce((count, item) => count + item.quantity, 0),
    value:
      items.reduce(
        (sum, item) => sum + item.unitPriceCents * item.quantity,
        0,
      ) / 100,
    currency: "USD",
  });
}

function trackCheckoutRedirected(
  surface: DirectCheckoutRequest["source"],
  guest: boolean,
  startedAt: number,
) {
  track("checkout.redirected", {
    surface,
    guest,
    durationMs: Math.round(performance.now() - startedAt),
  });
}

export type CheckoutStartSkipCode =
  | "no_location"
  | "checkout_state_loading"
  | "no_shipments"
  | "contact_dismissed";

export type DirectCheckoutOutcome = "started" | "refused" | "dismissed";

export function trackStartSkipped(
  surface: DirectCheckoutRequest["source"],
  code: CheckoutStartSkipCode,
) {
  track("checkout.blocked", { stage: "start", surface, code, reason: null });
}

function reportCheckoutRefused(
  thrown: unknown,
  surface: DirectCheckoutRequest["source"],
) {
  const handled = handleError(thrown, {
    display: "banner",
    fallbackMessage: "Could not start checkout",
    reportIssueContext: { titlePrefix: "Checkout failed" },
  });
  track("checkout.blocked", {
    stage: "start",
    surface,
    code: handled.code,
    reason: handled.message,
  });
}

function guestStartArgs<Delivery extends object>(
  delivery: Delivery,
  request: DirectCheckoutRequest,
  contact: GuestCheckoutContact,
) {
  return {
    ...delivery,
    contact,
    items: request.items.map(({ productId, quantity }) => ({
      productId,
      quantity,
    })),
    guestSessionId: getGuestCheckoutSessionId(),
    returnToListing: request.source === "listing",
  };
}

function memberStartArgs<Delivery extends object>(
  delivery: Delivery,
  request: DirectCheckoutRequest,
) {
  return {
    ...delivery,
    productIds:
      request.source === "listing"
        ? request.items.map((item) => item.productId)
        : undefined,
    buyNowLine: request.buyNowLine,
  };
}

function checkoutDelivery(request: DirectCheckoutRequest) {
  return {
    ...deliveryFromShipments(request.shipments),
    shipments: request.shipments,
    expectedFreightCents: request.expectedFreightCents,
    expectedProductSubtotals: request.expectedProductSubtotals,
    posthogDistinctId: readPosthogDistinctId(),
  };
}

export function useDirectCheckout() {
  const { isAuthenticated } = useConvexAuth();
  const startGuest = useAction(api.cart.guestCheckout.startGuestCartCheckout);
  const startMember = useAction(api.cart.checkout.startCartCheckout);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const guestContact = useGuestContactPrompt();
  const requestContact = guestContact.request;

  const startCheckout = useCallback(
    async (request: DirectCheckoutRequest): Promise<DirectCheckoutOutcome> => {
      if (request.shipments.length === 0) {
        trackStartSkipped(request.source, "no_shipments");
        return "refused";
      }
      const contact = isAuthenticated ? null : await requestContact(request.contact);
      if (!isAuthenticated && !contact) {
        trackStartSkipped(request.source, "contact_dismissed");
        return "dismissed";
      }
      setIsRedirecting(true);
      trackCheckoutStarted(request.items, request.source);
      const startedAt = performance.now();
      const rowsAtCheckout =
        request.source === "cart" && !isAuthenticated ? checkoutRowsNow() : [];
      try {
        const delivery = checkoutDelivery(request);
        const result = contact
          ? await startGuest(guestStartArgs(delivery, request, contact))
          : await startMember(memberStartArgs(delivery, request));
        if (!result.nextCheckoutUrl) {
          throw new Error("Payment could not be opened. Please try again.");
        }
        if (!isAuthenticated) {
          rememberCheckoutRows(result.checkoutAttemptId, rowsAtCheckout);
        }
        trackCheckoutRedirected(request.source, !isAuthenticated, startedAt);
        window.location.assign(result.nextCheckoutUrl);
        return "started";
      } catch (thrown) {
        reportCheckoutRefused(thrown, request.source);
        setIsRedirecting(false);
        return "refused";
      }
    },
    [isAuthenticated, requestContact, startGuest, startMember],
  );

  return { startCheckout, isRedirecting, contactPrompt: guestContact.prompt };
}
